import { Button, Icon, Li, Link, Typography, Ul } from '@vp/swan'
import type { NavigationCategory } from '../../../../types/navigation'
import { getHolidayThemeStyles } from '../../../../utils/navigation'
import { getHeaderTracker } from '../../../../tracking'
import { TOP_NAV, TOP_NAV_LEVEL } from '../../../../constants/analytics'
import BadgeWithText from '../../BadgeWithText'
import { useI18n } from '../../../../context/TranslationContext'
import { useNavContext } from '../../../../context/NavContextProvider'

type NavMobileMenuProps = Readonly<{
  navigationData: NavigationCategory[]
  openNavPanel: (title: string, id: string) => void
}>

export default function NavMobileMenu({ navigationData, openNavPanel }: NavMobileMenuProps) {
  if (!navigationData?.length) return null

  return (
    <Ul skin="minimal" style={{ gap: 0 }}>
      {navigationData.map((link) => (
        <NavMenuItem key={link.id} navItem={link} openNavPanel={openNavPanel} />
      ))}
    </Ul>
  )
}

type NavMenuItemProps = Readonly<{
  navItem: NavigationCategory
  openNavPanel: (title: string, id: string) => void
}>

function NavMenuItem({ navItem, openNavPanel }: NavMenuItemProps) {
  const { translate } = useI18n()
  const { hoverNavItem, isMxNav } = useNavContext()
  const holidayThemeStyle = getHolidayThemeStyles(navItem.theme)

  const trackClick = () => {
    hoverNavItem(navItem.id, true)
    const navigationDetailPath = navItem.analyticsId ? [navItem.analyticsId] : []
    const subSection = TOP_NAV
    const navigationSection = isMxNav ? TOP_NAV_LEVEL : undefined

    getHeaderTracker().trackHeaderLink({
      subSection,
      navigationDetailPath,
      navigationSection,
    })
  }

  // Leaf items (no children) render as direct navigation links
  if (!navItem.children?.length) {
    return (
      <Li>
        <Link
          skin="unstyled"
          width="full-width"
          py={4}
          href={navItem.url}
          onClick={trackClick}
          onKeyDown={(event: React.KeyboardEvent) => ['Enter', ' '].includes(event.key) && hoverNavItem(navItem.id, true)}
          style={holidayThemeStyle}
        >
          <Typography fontSkin="body-standard-bold">
            <BadgeWithText badge={navItem.badge} text={navItem.text} />
          </Typography>
        </Link>
      </Li>
    )
  }

  // Items with children render as buttons that open a drill-down panel
  const handleDrillDown = () => {
    trackClick()
    hoverNavItem(navItem.id, true)
    openNavPanel(navItem.text, navItem.id)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleDrillDown()
    }
  }

  return (
    <Li>
      <Button
        skin="unstyled"
        display="flex"
        width="full-width"
        style={{ justifyContent: 'space-between', ...holidayThemeStyle }}
        py={4}
        onClick={handleDrillDown}
        onKeyDown={handleKeyDown}
        aria-haspopup="true"
      >
        <Typography fontSkin="body-standard-bold">
          <BadgeWithText badge={navItem.badge} text={navItem.text} />
        </Typography>
        {isMxNav && <Icon size="standard" iconType="caretRight" aria-label={translate('menu.showSub')} />}
      </Button>
    </Li>
  )
}
